import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Search, Plus } from 'lucide-react';
import { Zone } from '../../services/zone';

interface SearchableZoneDropdownProps {
  zones: Zone[];
  selectedZone: Zone | null;
  onZoneChange: (zone: Zone | null) => void;
  onCustomZoneAdd: (customZoneName: string) => void;
  placeholder?: string;
  className?: string;
}

const SearchableZoneDropdown: React.FC<SearchableZoneDropdownProps> = ({
  zones,
  selectedZone,
  onZoneChange,
  onCustomZoneAdd,
  placeholder = "Search and select a zone...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredZones, setFilteredZones] = useState<Zone[]>(zones);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customZoneName, setCustomZoneName] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filtered = zones.filter(zone =>
      zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (zone.description && zone.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredZones(filtered);
  }, [zones, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomInput(false);
        setCustomZoneName('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleZoneSelect = (zone: Zone) => {
    onZoneChange(zone);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCustomZoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customZoneName.trim()) {
      onCustomZoneAdd(customZoneName.trim());
      setCustomZoneName('');
      setShowCustomInput(false);
      setIsOpen(false);
    }
  };

  const handleRemoveZone = () => {
    onZoneChange(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Zone Display */}
      {selectedZone && (
        <div className="mb-2">
          <div className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
            <span className="truncate max-w-[200px]">{selectedZone.name}</span>
            <button
              type="button"
              onClick={handleRemoveZone}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Dropdown Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <span className={`block truncate ${selectedZone ? 'text-gray-900' : 'text-gray-500'}`}>
            {selectedZone ? selectedZone.name : placeholder}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </span>
        </button>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {/* Search Input */}
          <div className="sticky top-0 z-10 bg-white px-3 py-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Search zones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Zone List */}
          {filteredZones.length > 0 ? (
            filteredZones.map((zone) => (
              <div
                key={zone.id}
                className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-blue-50"
                onClick={() => handleZoneSelect(zone)}
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{zone.name}</div>
                  {zone.description && (
                    <div className="text-sm text-gray-500 truncate">{zone.description}</div>
                  )}
                  {zone.department && (
                    <div className="text-xs text-gray-400">{zone.department.name}</div>
                  )}
                </div>
                {selectedZone?.id === zone.id && (
                  <span className="text-blue-600">✓</span>
                )}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500">No zones found.</div>
          )}

          {/* Add Custom Zone Option */}
          <div className="border-t border-gray-200">
            {!showCustomInput ? (
              <button
                type="button"
                onClick={() => setShowCustomInput(true)}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add custom zone
              </button>
            ) : (
              <form onSubmit={handleCustomZoneSubmit} className="px-4 py-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customZoneName}
                    onChange={(e) => setCustomZoneName(e.target.value)}
                    placeholder="Enter zone name..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomZoneName('');
                    }}
                    className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableZoneDropdown;

